import React from 'react';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  useAddClientArtifactMutation,
  useAddClientFileMutation,
  usePickableArtifactsQuery,
  useSearchExistingFilesQuery
} from '~/api/graphql';
import { FormError } from '~/components/form-error';
import { PickerRow, PickerSection } from '~/components/clients/picker-row';
import { useDebouncedValue } from '~/hooks/use-list-controls';
import { dateFormatter } from '~/components/table-formatters';

/** Enough to choose from; the search box is how you reach the rest. */
const PICKER_LIMIT = 8;

const ATTACH_FAILED = 'Could not attach that document.';

/**
 * Attaches a document to a client that its linked workspaces do not supply.
 *
 * `kind` is the tab it opens from: artifacts Cara generated on Documents, files
 * someone uploaded on Uploads. uploadedOnly keeps out knowledge-graph entities,
 * which carry no agency_file id for addClientFile to take.
 */
export const LinkDocumentDialog = ({
  open,
  clientId,
  kind,
  onClose,
  onAttached
}: {
  open: boolean;
  clientId: string;
  kind: 'artifact' | 'file';
  onClose: () => void;
  onAttached: () => void;
}) => {
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [error, setError] = React.useState<string | null>(null);

  const [addArtifact, { loading: addingArtifact }] = useAddClientArtifactMutation();
  const [addFile, { loading: addingFile }] = useAddClientFileMutation();
  const attaching = addingArtifact || addingFile;

  const { data: artifactData, loading: artifactsLoading } = usePickableArtifactsQuery({
    variables: { filter: { search: debouncedSearch.trim(), limit: PICKER_LIMIT } },
    skip: !open || kind !== 'artifact'
  });
  // addClientFile takes only an agency_file row, and the AMS half costs a scored
  // search per integration.
  const { data: fileData, loading: filesLoading } = useSearchExistingFilesQuery({
    variables: { filters: { search: debouncedSearch.trim(), uploadedOnly: true } },
    skip: !open || kind !== 'file'
  });

  React.useEffect(() => {
    if (!open) {
      setSearch('');
      setError(null);
    }
  }, [open]);

  const attach = async (run: () => Promise<{ errors?: readonly unknown[] | null }>) => {
    setError(null);
    const message = messageOfFailure(await run(), ATTACH_FAILED);
    if (message) {
      setError(message);
      return;
    }
    onAttached();
    onClose();
  };

  const attachButton = (run: () => Promise<{ errors?: readonly unknown[] | null }>) => (
    <Button outline disabled={attaching} onClick={() => attach(run)}>
      Attach
    </Button>
  );

  // Both halves arrive searched and bounded by the server. Narrowing again here
  // would apply a second filter that disagrees with the one that produced them.
  const artifacts = kind === 'artifact' ? (artifactData?.caraWorkspaceArtifacts ?? []) : [];
  const files = kind === 'file' ? (fileData?.searchFiles ?? []).slice(0, PICKER_LIMIT) : [];

  const loading = artifactsLoading || filesLoading;
  const empty = artifacts.length === 0 && files.length === 0;

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle>Attach a document</DialogTitle>
      <DialogDescription>
        For a document this client&apos;s workspaces do not already supply. It stays where it lives;
        this records that it belongs here too.
      </DialogDescription>
      <DialogBody>
        <InputGroup>
          <MagnifyingGlassIcon className="h-4 w-4" />
          <Input
            autoFocus
            type="search"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </InputGroup>

        <div className="mt-4 min-h-48">
          {loading && empty ? (
            <div className="py-12 text-center">
              <Spinner size="size-6" color="neutral" />
            </div>
          ) : empty ? (
            <p className="py-12 text-center text-sm text-neutral-500 dark:text-neutral-400">
              {search ? 'No documents match that search.' : 'No documents to attach.'}
            </p>
          ) : (
            <>
              {artifacts.length > 0 && (
                <PickerSection heading="Generated">
                  {artifacts.map((artifact) => (
                    <PickerRow
                      key={artifact.id}
                      name={artifact.title || 'Untitled document'}
                      detail={[
                        artifact.metadata?.workspaceTitle,
                        dateFormatter.format(new Date(artifact.createdAt))
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      action={attachButton(() =>
                        addArtifact({ variables: { clientId, artifactId: artifact.id } })
                      )}
                    />
                  ))}
                </PickerSection>
              )}
              {files.length > 0 && (
                <PickerSection heading="Uploaded">
                  {files.map((file) => (
                    <PickerRow
                      key={file.id}
                      name={file.name}
                      action={attachButton(() =>
                        addFile({ variables: { clientId, fileId: file.id } })
                      )}
                    />
                  ))}
                </PickerSection>
              )}
            </>
          )}
        </div>

        <FormError message={error} className="mt-2 text-sm text-red-600 dark:text-red-400" />
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
